import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProjectFormService } from '../../../services/projectform.service';
import { ProjectFormModel } from '../../../models/projectform.model';
import { CreateFormModalComponent } from './create-form-modal/create-form-modal';
import { ProjectStateService } from '../../../services/project-state.service';
import { PermissionHelperService } from '../../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../../constants/permission.constants';
import { SSOService } from '../../../services/sso.service';
import { ROLE_NAMES } from '../../../constants/role.constants';

@Component({
  selector: 'app-form-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    DialogModule,
    InputTextModule
  ],
  providers: [MessageService, DialogService],
  templateUrl: './form-detail.html',
  styleUrl: './form-detail.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormDetailComponent implements OnInit {
  private readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  folderId!: number;
  projectId?: number;
  forms: ProjectFormModel[] = [];
  filteredForms: ProjectFormModel[] = [];
  templateName: string = '';
  loading: boolean = false;
  searchValue: string = '';
  createFormDialogRef?: DynamicDialogRef;
  showCloneDialog = false;
  cloneFormName = '';
  cloneSourceFormId: number | null = null;
  showRenameDialog = false;
  renameFormName = '';
  renameFormId: number | null = null;
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canDelete: boolean = false;
  isProjectInactive: boolean = false;

  // Sort state
  sortField: string = '';
  sortOrder: 'asc' | 'desc' | '' = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectFormService: ProjectFormService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService,
    private ssoService: SSOService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'] ? +params['projectId'] : undefined;
      this.folderId = +params['folderId'];
      if (this.folderId) {
        this.tryLoadForms();
      }
    });

    this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.VIEW);
      this.canCreate = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.CREATE);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.DELETE);
      this.updateReadOnlyState();
      this.tryLoadForms();
      this.cdr.markForCheck();
    });

    this.projectStateService.pageSearch$.subscribe(searchText => {
      this.searchValue = searchText;
      this.applyFilterAndSort();
      this.cdr.markForCheck();
    });
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || !this.canCreate;
    this.cdr.markForCheck();
  }

  private tryLoadForms(): void {
    if (!this.folderId || !this.canView) return;
    this.loadForms();
  }

  loadForms(): void {
    this.loading = true;
    this.projectFormService.getProjectForms(this.folderId).subscribe({
      next: (forms) => {
        this.forms = forms;
        this.applyFilterAndSort();
        if (forms.length > 0) {
          this.templateName = forms[0].templateName || '';
          if (!this.projectId) {
            this.projectId = forms[0].projectId;
          }
        }
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading forms:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load forms'
        });
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ===== Sort =====
  onSort(field: string): void {
    if (this.sortField === field) {
      if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc';
      } else if (this.sortOrder === 'desc') {
        this.sortField = '';
        this.sortOrder = '';
      }
    } else {
      this.sortField = field;
      this.sortOrder = 'asc';
    }
    this.applyFilterAndSort();
    this.cdr.markForCheck();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return 'ri-arrow-up-down-line';
    }
    return this.sortOrder === 'asc' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line';
  }

  formatDatabaseDate(value?: string | Date | null): string {
    if (!value) {
      return '-';
    }

    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
      return '-';
    }

    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    if (monthIndex < 0 || monthIndex >= this.monthNames.length || !Number.isFinite(day)) {
      return '-';
    }

    return `${this.monthNames[monthIndex]} ${day.toString().padStart(2, '0')}, ${match[1]}`;
  }

  private applyFilterAndSort(): void {
    let result = [...this.forms];

    // Filter
    if (this.searchValue) {
      const query = this.searchValue.toLowerCase();
      result = result.filter(f =>
        f.name?.toLowerCase().includes(query) ||
        f.templateNameId?.toString().includes(query) ||
        f.assignedByName?.toLowerCase().includes(query) ||
        f.status?.toLowerCase().includes(query)
      );
    }

    // Sort
    if (this.sortField && this.sortOrder) {
      const field = this.sortField as keyof ProjectFormModel;
      const dir = this.sortOrder === 'asc' ? 1 : -1;
      result.sort((a, b) => {
        const aVal = a[field] ?? '';
        const bVal = b[field] ?? '';
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return (aVal - bVal) * dir;
        }
        return String(aVal).localeCompare(String(bVal)) * dir;
      });
    }

    this.filteredForms = result;
  }

  goBack(): void {
    if (this.projectId) {
      this.router.navigate(['/forms', this.projectId]);
    } else {
      window.history.back();
    }
  }

  openCreateFormModal(): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    const dialogRef = this.dialogService.open(CreateFormModalComponent, {
      header: `${this.templateName || 'Form'}`,
      width: '500px',
      modal: true,
      closable: true,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'auto',
        padding: '0'
      },
      data: {
        folderId: this.folderId
      }
    });

    if (dialogRef) {
      this.createFormDialogRef = dialogRef;
      dialogRef.onClose.subscribe((result) => {
        if (result) {
          this.loadForms();
        }
      });
    }
  }

  openRenameDialog(form: ProjectFormModel): void {
    if (!this.canCreate || this.isProjectInactive) return;
    this.renameFormId = form.id;
    this.renameFormName = form.name || '';
    this.showRenameDialog = true;
  }

  saveRenameForm(): void {
    if (!this.renameFormId) return;
    const name = this.renameFormName.trim();
    if (!name) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid Data', detail: 'Form name is required' });
      return;
    }

    this.projectFormService.renameProjectForm(this.renameFormId, { name }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Form renamed successfully' });
        this.loadForms();
        this.showRenameDialog = false;
        this.renameFormName = '';
        this.renameFormId = null;
      },
      error: (error) => {
        console.error('Error renaming form:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to rename form' });
      }
    });
  }

  openCloneDialog(form: ProjectFormModel): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    this.cloneSourceFormId = form.id;
    this.cloneFormName = `${form.name || 'Form'}-copy`;
    this.showCloneDialog = true;
  }

  saveCloneForm(): void {
    if (!this.cloneSourceFormId) {
      return;
    }
    const name = this.cloneFormName.trim();
    if (!name) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid Data', detail: 'Form name is required' });
      return;
    }

    this.projectFormService.cloneProjectForm({
      sourceFormId: this.cloneSourceFormId,
      name
    }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Form cloned successfully' });
        this.loadForms();
        this.showCloneDialog = false;
        this.cloneFormName = '';
        this.cloneSourceFormId = null;
      },
      error: (error) => {
        console.error('Error cloning form:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to clone form' });
      }
    });
  }

  getStatusClass(status?: string): string {
    if (!status || status === 'Draft') return 'status-badge--secondary';
    return 'status-badge--success';
  }

  navigateToFormChecksheet(form: ProjectFormModel): void {
    if (form.projectId && form.id) {
      this.router.navigate(['/form-checksheet', form.projectId, form.id]);
    }
  }

  private isAdminOrSuperAdmin(): boolean {
    const role = (this.ssoService.getCurrentUser()?.roleName ?? '').trim().toLowerCase();
    return role === ROLE_NAMES.ADMIN || role === ROLE_NAMES.SUPER_ADMIN;
  }

  deleteForm(form: ProjectFormModel): void {
    if (this.isProjectInactive) return;

    const status = form.status ?? 'Draft';
    const isDraft = status === 'Draft';

    if (!isDraft && !this.isAdminOrSuperAdmin()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Access Denied',
        detail: `Only Admin or Super Admin can delete forms in ${status} state.`
      });
      return;
    }

    const message = isDraft
      ? `Are you sure you want to delete "${form.name}"? This action cannot be undone.`
      : `"${form.name}" is in ${status} state. All responses and uploaded files will be permanently deleted. Are you sure?`;

    if (!confirm(message)) return;

    this.projectFormService.deleteProjectForm(form.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `"${form.name}" deleted successfully.` });
        this.loadForms();
      },
      error: (error) => {
        const detail = error?.error?.message || 'Failed to delete form.';
        this.messageService.add({ severity: 'error', summary: 'Error', detail });
      }
    });
  }
}
